import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle, RefreshCw, LogOut, Loader, ShieldX } from 'lucide-react';
import AuthButton from './AuthButton';

const AccessGuard = ({ children }) => {
  const { 
    isAuthenticated, 
    validandoAcceso, 
    accesoAutorizado, 
    mensajeAcceso, 
    verificarAccesoUsuario, 
    logout,
    user
  } = useAuth();

  // Función para reintentar validación
  const handleReintentar = async () => {
    if (user) {
      const account = user;
      let emailParaValidar = null;

      // Intentar extraer el email/SAM account
      if (account.idTokenClaims?.samAccountName) {
        emailParaValidar = account.idTokenClaims.samAccountName.toLowerCase().trim();
      } else if (account.username && account.username.includes('@')) {
        emailParaValidar = account.username.split('@')[0].toLowerCase().trim();
      } else if (account.username) {
        emailParaValidar = account.username.toLowerCase().trim();
      }

      if (emailParaValidar) {
        await verificarAccesoUsuario(emailParaValidar);
      }
    }
  };

  // Si el usuario no está autenticado
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mb-6">
            <ShieldX className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Acceso Requerido
            </h1>
            <p className="text-gray-600 mb-6">
              Debe iniciar sesión para acceder a esta aplicación.
            </p>
            
            {/* Botón de Iniciar Sesión */}
            <div className="flex justify-center">
              <AuthButton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si está validando acceso
  if (validandoAcceso) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mb-6">
            <Loader className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verificando Acceso
            </h1>
            <p className="text-gray-600">
              {mensajeAcceso || 'Validando permisos de usuario...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si el usuario está autenticado pero NO autorizado
  if (isAuthenticated && !accesoAutorizado) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center border-t-4 border-red-500">
          <div className="mb-6">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Acceso Denegado
            </h1>
            <div className="text-red-600 font-medium mb-2">
              Usuario sin autorización
            </div>
            <p className="text-gray-600 text-sm mb-4">
              {mensajeAcceso || 'No tiene permisos para acceder a esta aplicación.'}
            </p>
            
            {user && (
              <div className="bg-gray-50 rounded-md p-3 mb-6 text-sm">
                <div className="text-gray-700">
                  <strong>Usuario conectado:</strong> {user.name || user.username}
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  {user.email || user.username}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleReintentar}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar Validación
            </button>
            
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              Cerrar Sesión
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-600">
              Si cree que esto es un error, contacte al administrador del sistema.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si está autenticado Y autorizado, mostrar el contenido SIN WRAPPER
  // CRÍTICO: No agregamos ningún contenedor que interfiera con el layout original
  if (isAuthenticated && accesoAutorizado) {
    return children;
  }

  // Estado de fallback
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="mb-6">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Estado Indeterminado
          </h1>
          <p className="text-gray-600">
            Ocurrió un error inesperado. Intente recargar la página.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          Recargar Página
        </button>
      </div>
    </div>
  );
};

export default AccessGuard;